
import { Card, CardContent } from "@/components/ui/card";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, MoreHorizontal } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "@/lib/router-compat";
import type { Database } from "@/integrations/supabase/types";

type UserPost = Database['public']['Tables']['user_posts']['Row'] & {
  user_profiles: Database['public']['Tables']['user_profiles']['Row'];
};

const PostCard = ({ post }: { post: UserPost }) => {
  const timeAgo = formatDistanceToNow(new Date(post.created_at ?? Date.now()), { addSuffix: true });

  return (
    <Card className="hover:bg-muted/30 transition-colors">
      <CardContent className="p-4">
        <div className="flex gap-3">
          <Link to={`/profile/${post.user_id}`}>
            <ProfileAvatar
              userId={post.user_id}
              avatarUrl={post.user_profiles?.avatar_url}
              name={post.user_profiles?.full_name}
              background={post.user_profiles?.avatar_background}
              size="md"
            />
          </Link>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Link to={`/profile/${post.user_id}`} className="font-semibold text-sm hover:underline flex items-center gap-1">
                {post.user_profiles?.full_name || "Unknown User"}
              </Link>
              <Link to={`/profile/${post.user_id}`} className="text-muted-foreground text-sm hover:underline">
                @{post.user_profiles?.username || "user"}
              </Link>
              <span className="text-muted-foreground text-sm">·</span>
              <span className="text-muted-foreground text-sm">{timeAgo}</span>
              
              <Button variant="ghost" size="sm" className="ml-auto h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="text-sm leading-relaxed mb-3 whitespace-pre-wrap">
              {post.content}
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" className="h-8 px-2 text-muted-foreground hover:text-blue-600">
                <MessageCircle className="h-4 w-4 mr-1" />
                <span className="text-xs">{post.replies_count}</span>
              </Button>
              
              <Button variant="ghost" size="sm" className="h-8 px-2 text-muted-foreground hover:text-destructive">
                <Heart className="h-4 w-4 mr-1" />
                <span className="text-xs">{post.likes_count}</span>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PostCard;
